<template>
    <div>
        <CRMOrderStateControlWrapper v-slot="{ orderStateControlVisible }">
            <div
                ref="editor"
                :class="[
                    'editor editor--light',
                    {
                        'editor--embed': isEmbed,
                        'editor--fullheight': isEmbed && !orderStateControlVisible,
                    },
                ]"
            >
                <Spinner v-if="!book || !book.id" class="spinner--opaque" />

                <EditorLightText
                    ref="EditorText"
                    :pages="pages"
                    :playing-part="playingPart"
                    @search="
                        fetchInitialBookparts({
                            ...$event,
                            select: true,
                        })
                    "
                    @playPart="onPlayPart"
                    @toPage="handlePageNavigation"
                >
                    <template #bottom>
                        <div class="editor__player editor__player--new">
                            <CharsInfo v-if="book.id" :book="book.id" />
                            <div v-if="pages.length" class="editor__player-inner">
                                <div class="editor__player-player">
                                    <PartsPlayer
                                        ref="partsPlayer"
                                        :total-pages="totalPages"
                                        @playingPartChanged="onPlayingPartChanged"
                                        @playingNextPart="onPlayingNextPart"
                                    >
                                        <template #after-player>
                                            <BooksBuilds
                                                v-if="!isEmbed && book.id"
                                                :book-id="book.id"
                                                checking-parts
                                                @openBrokenEditor="redirectToBrokenParts"
                                            />
                                        </template>
                                    </PartsPlayer>
                                </div>
                            </div>
                        </div>
                    </template>
                </EditorLightText>
            </div>
        </CRMOrderStateControlWrapper>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

import EditorLightText from '../EditorLightText/EditorLightText';
import PartsPlayer from '../PartsPlayer/PartsPlayer';
import CRMOrderStateControlWrapper from '../../components/CRMOrderStateControl/CRMOrderStateControlWrapper';
import CharsInfo from '../CharsInfo/CharsInfo.vue';

import { clickOutside } from '@speechki-frontend/shared/helpers/directives/clickOutside';

import { getEditProgress, saveEditProgress, writeEditProgress } from '../../services/editProgress';

export default {
    name: 'Editor',
    components: {
        PartsPlayer,
        EditorLightText,
        CRMOrderStateControlWrapper,
        CharsInfo,
    },
    directives: {
        clickOutside,
    },
    inject: {
        isEmbed: {
            default: true,
        },
    },
    provide() {
        return {
            setActivePage: this.setActivePage,
            fetchInitialPartsSub: undefined,
            isEmbed: this.isEmbed,
        };
    },
    inheritAttrs: false,
    data() {
        return {
            playingPart: undefined,
            //
            textContainer: undefined,
        };
    },
    computed: {
        ...mapState({
            book: (state) => state.book.book,
        }),
        ...mapGetters(['isBookTrial', 'isAdmin', 'isPartner', 'isReviewer']),
        //
        ...mapState('pagedBookparts', {
            pages: (state) => state.pages,
            loading: (state) => state.loading,
            activePage: (state) => state.activePage,
        }),
        ...mapGetters('pagedBookparts', [
            'totalPages',
            'canLoadNext',
            'canLoadPrev',
            'isPartInActivePage',
        ]),
    },
    watch: {
        '$route.query'() {
            saveEditProgress({
                bookId: this.book.id || this.$route.params.id,
                ...this.$route.query,
            });
        },
        activePage(page) {
            this.$router
                .replace({
                    ...this.$route,
                    query: {
                        ...this.$route.query,
                        page: page,
                    },
                })
                .catch(() => {});
        },
    },
    mounted() {
        window.onbeforeunload = () => {
            writeEditProgress();
        };
        this.textContainer = document.querySelector('.text__inner');

        // subsribe to initial parts fetching to manage scroll
        let scrollLock = 0;
        this.fetchInitialPartsSub = this.$store.subscribeAction({
            before: ({ payload = {} }) => {
                if (payload.preserveScroll) {
                    scrollLock = this.textContainer.scrollTop;
                }
            },
            after: ({ type, payload = {} }) => {
                if (payload.preserveScroll) {
                    this.textContainer.scrollTop = scrollLock;
                    return;
                }

                if (type === 'pagedBookparts/fetchInitialBookparts') {
                    this.scrollAfterInitialFetch(payload);
                }
            },
        });
    },
    async created() {
        const { query, params } = this.$route;

        if (this.book && parseInt(params.id) !== this.book.id) {
            this.clearBook();
            this.clearBookparts();
        }

        if (!query || !query.page) {
            let lastState = getEditProgress(params.id);

            let query = {
                page: 1,
            };

            if (lastState) {
                query = lastState;
                delete query.bookId;
                delete query.timestamp;
            }

            this.$router
                .replace({
                    name: this.$route.name,
                    params: { id: params.id },
                    query,
                })
                .catch(() => {});
        }

        try {
            await this.fetchBook(params.id);
        } catch (err) {
            console.log(err);
            this.$router.replace({ name: '404' });
        }

        if (!this.book.id) return;

        await this.fetchInitialBookparts({ page: this.$route.query.page });
    },
    beforeUnmount() {
        this.$store.commit('CLEAR_BOOK');
        writeEditProgress();
        this.clearBookparts();

        if (this.fetchInitialPartsSub) {
            this.fetchInitialPartsSub();
        }
    },
    methods: {
        ...mapActions(['fetchBook', 'fetchLimits', 'clearBook']),
        //
        ...mapActions('pagedBookparts', ['fetchInitialBookparts', 'clearBookparts']),

        onPlayingPartChanged(part) {
            this.playingPart = part;
        },

        onPlayingNextPart(part) {
            const { type, partId } = part;
            if (type === 'audio') {
                this.$nextTick(() => {
                    this.$refs.EditorText.scrollToPart(partId);
                });
            }
        },

        onPlayPart(event) {
            if (this.$refs.partsPlayer) {
                this.$refs.partsPlayer.makePlaylist({
                    page: this.activePage,
                    partId: event.id,
                    playImmediately: true,
                });
            }
        },

        stopPartsPlayer() {
            if (this.$refs.partsPlayer) {
                this.$refs.partsPlayer.pause();
            }
        },

        scrollAfterInitialFetch({ id, select = false }) {
            this.playingPart = undefined;

            const { part } = this.$route.query;
            const target = id || part;

            if (target && this.isPartInActivePage(target)) {
                this.$nextTick(() => {
                    if (select) {
                        this.$refs.selector.select({ id });
                        return;
                    }

                    this.$refs.EditorText.scrollToPart(target);
                });
                return;
            }

            this.$nextTick(() => {
                this.$scrollTo(document.querySelector('.editor-text-page.is-active'), 250, {
                    container: this.textContainer,
                    offset: -60,
                });
            });
        },

        // when no fetchInitialBookparts called, use this to set active page (eg pagination)
        // it is a copy of vuex action, but it works faster here and doesn't mess up scrolling
        async setActivePage(page) {
            page = Number(page);

            this.$store.commit('pagedBookparts/SET_ACTIVE_PAGE', page);

            if (page < this.totalPages && this.canLoadNext(page)) {
                await this.$store.dispatch('pagedBookparts/fetchNextPage', page);

                if (this.pages.length > 3) {
                    const scrollLock =
                        this.textContainer.scrollHeight - this.textContainer.scrollTop;

                    this.$store.commit('pagedBookparts/SHIFT_PAGE');

                    this.$nextTick(() => {
                        this.textContainer.scrollTop = this.textContainer.scrollHeight - scrollLock;
                    });
                }
            }

            if (page === this.pages[0].id && this.canLoadPrev(page)) {
                await this.$store.dispatch('pagedBookparts/fetchPrevPage', page);

                if (this.pages.length > 3) {
                    this.$store.commit('pagedBookparts/POP_PAGE');

                    this.$nextTick(() => {
                        const newPage = document.querySelector(
                            `[data-page="${this.activePage - 1}"]`
                        );

                        this.textContainer.scrollTop =
                            this.textContainer.scrollTop + newPage.offsetHeight;
                    });
                }
            }
        },

        async handlePageNavigation(page) {
            if (page > this.totalPages) {
                return;
            }

            const query = {
                ...this.$route.query,
                page,
            };

            if (query.part) {
                delete query.part;
            }

            this.$router
                .push({
                    ...this.$route,
                    query,
                })
                .catch(() => {});

            await this.fetchInitialBookparts({ page: +page });
        },

        redirectToBrokenParts() {
            this.$router
                .push({
                    name: `${this.isEmbed ? 'embed-' : ''}broken-parts`,
                    params: {
                        id: this.book.id,
                    },
                })
                .catch(() => {});
        },
    },
};
</script>

<style lang="scss" src="./EditorLight.scss"></style>
